import { useState, useEffect, useRef } from 'react';
import './Admin.css';
import Toast from '../components/Toast';
import useStore from '../store/store';
import { apiService } from '../services/api';

function Admin() {
  const originalBodyClass = useRef('');
  const [formData, setFormData] = useState({
    userid: '',
    password: '',
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [toast, setToast] = useState(null);
  const [selectedPlayer, setSelectedPlayer] = useState('');
  const [selectedSeason, setSelectedSeason] = useState('');
  const [selectedTeam, setSelectedTeam] = useState('');
  const [selectedGroup, setSelectedGroup] = useState('');
  const [activeTab, setActiveTab] = useState('match-data');
  const [auctionSetup, setAuctionSetup] = useState({
  season_id: '',
  num_sets: 5,
  budget_per_team: 150,
  admin_password: '',
  });
 const [auctionSetupResult, setAuctionSetupResult] = useState(null);
 const [isRunningSetup, setIsRunningSetup] = useState(false);

  const [isUpdating, setUpdating] = useState(false);
  const [availableMatches, setAvailableMatches] = useState([]);
  const [selectedAvailableMatch, setSelectedAvailableMatch] = useState('');
  const [isLoadingMatches, setIsLoadingMatches] = useState(false);
  const [sponsors, setSponsors] = useState([]);
  const [sponsorForm, setSponsorForm] = useState({
    name: '',
    website: '',
    instagram: '',
    logo: '',
    is_active: true,
  });
  const [isSavingSponsor, setIsSavingSponsor] = useState(false);
  const [isEditingSponsor, setIsEditingSponsor] = useState(false);
  const [editingSponsorId, setEditingSponsorId] = useState(null);

  // Fixture Management States
  const [fixtureData, setFixtureData] = useState({
    season_id: '',
    group_id: '',
    attacker_team_id: '',
    defender_team_id: '',
    match_datetime: '',
    venue: 'ONLINE',
    venue_location: '',
    youtube_location: '',
  });
  const [selectedFixture, setSelectedFixture] = useState(null);
  const [fixtures, setFixtures] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);

  // State for store data
  const [seasons, setSeasons] = useState([]);
  const [teams, setTeams] = useState([]);
  const [groups, setGroups] = useState([]);
  const [filteredPlayers, setFilteredPlayers] = useState([]);
  const [verificationPlayers, setVerificationPlayers] = useState([]);
  const [verificationLoading, setVerificationLoading] = useState(false);
  const [verificationSeason, setVerificationSeason] = useState('');
  const [verificationSearch, setVerificationSearch] = useState('');

  // Toast functions
  const showToast = (message, type = 'info') => {
    setToast({ message, type, id: Date.now() });
  };

  const hideToast = () => {
    setToast(null);
  };

  const loadVerificationPlayers = async (seasonId) => {
    if (!seasonId) return;
    setVerificationLoading(true);
    try {
      const res = await apiService.getSeasonPlayers();
      const filtered = (res.data || []).filter(p => p.season_id === seasonId);
      setVerificationPlayers(filtered);
    } catch (e) {
      showToast('Failed to load players', 'error');
    } finally {
      setVerificationLoading(false);
    }
  };

  const handleVerifyToggle = async (seasonPlayerId, currentVerified, gameName) => {
    try {
      await apiService.verifyPlayer(seasonPlayerId, !currentVerified);
      setVerificationPlayers(prev =>
        prev.map(p => p.id === seasonPlayerId ? { ...p, verified: !currentVerified } : p)
      );
      showToast(`${gameName} ${!currentVerified ? 'verified ✓' : 'unverified'}`, 'success');
    } catch (e) {
      showToast('Verification update failed', 'error');
    }
  };

  useEffect(() => {
    const bodyElement = document.querySelector('body');
    originalBodyClass.current = bodyElement.className;
    bodyElement.className = 'no-bg';

    return () => {
      bodyElement.className = originalBodyClass.current;
    };
  }, []);

  // Subscribe to store changes and load initial data
  useEffect(() => {
    const currentState = useStore.getState();

    // Seasons — fetch if not in store yet, subscribe for async update
    if (currentState.seasons && currentState.seasons.length > 0) {
      setSeasons(currentState.seasons);
    } else {
      currentState.fetchSeasons();
    }

    // Teams — fetch if not in store yet
    if (currentState.teams && currentState.teams.length > 0) {
      setTeams(currentState.teams);
    } else {
      currentState.fetchTeams();
    }

    // SeasonTeams — fetch if not in store yet
    if (!currentState.seasonTeams || Object.keys(currentState.seasonTeams).length === 0) {
      currentState.fetchSeasonTeams();
    }

    // Load groups
    loadGroups();

    // Subscribe so dropdowns populate when async fetches complete
    const unsubscribe = useStore.subscribe((state) => {
      if (state.seasons && state.seasons.length > 0) setSeasons([...state.seasons]);
      if (state.teams && state.teams.length > 0) setTeams([...state.teams]);
    });

    return () => unsubscribe();
  }, []);

  const loadGroups = async () => {
    try {
      const response = await apiService.getGroups();
      setGroups(response.data);
    } catch (error) {
      console.error('Error loading groups:', error);
    }
  };

  const loadSponsors = async () => {
    try {
      const response = await apiService.getSponsors(false);
      setSponsors(response.data || []);
    } catch (error) {
      console.error('Error loading sponsors:', error);
      setSponsors([]);
    }
  };

  useEffect(() => {
    if (isLoggedIn) {
      loadSponsors();
    }
  }, [isLoggedIn]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Required field validation
    if (!formData.userid.trim()) newErrors.userid = 'User ID is required';
    if (!formData.password.trim()) newErrors.password = 'Password is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Simulate login process - replace with actual authentication
      // For demo purposes, using simple credentials
      if (formData.userid === 'admin' && formData.password === 'adminvpl123') {
        showToast('Login successful!', 'success');
        setIsLoggedIn(true);
      } else {
        showToast('Invalid credentials. Please try again.', 'error');
      }
    } catch (error) {
      console.error('Login error:', error);
      showToast('Login failed. Please try again.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePlayerChange = (e) => {
    const playerId = e.target.value;
    console.log(playerId);
    setSelectedPlayer(playerId);
    setAvailableMatches([]);
    setSelectedAvailableMatch('');
  };

  const fetchPlayerMatches = async (puuid) => {
    setIsLoadingMatches(true);
    try {
      const response = await apiService.getPlayerMatchData(puuid);
      setAvailableMatches(response.data || []);
      if (response.data && response.data.length > 0) {
        showToast(`Found ${response.data.length} matches for this player`, 'success');
      } else {
        showToast('No matches found for this player', 'info');
      }
    } catch (error) {
      console.error('Error fetching player matches:', error);
      const errorMessage = error.response?.data?.Message || error.message || 'Failed to fetch player matches';
      showToast(`Error: ${errorMessage}`, 'error');
      setAvailableMatches([]);
    } finally {
      setIsLoadingMatches(false);
    }
  };

  const handleFetchPlayerMatches = () => {
    if (selectedPlayer) {
      fetchPlayerMatches(selectedPlayer);
    }
  };

  const handleSeasonChange = (e) => {
    const seasonId = e.target.value;
    setSelectedSeason(seasonId);
    setSelectedPlayer('');
    setSelectedTeam('');
    setAvailableMatches([]);
    setSelectedAvailableMatch('');

    // Update filtered players when season changes
    if (seasonId) {
      // Get teams for the selected season from the store
      const currentState = useStore.getState();
      const seasonTeamsData = currentState.seasonTeams[seasonId] || [];

      // Extract all players from all teams in this season
      const allPlayers = [];
      seasonTeamsData.forEach((team) => {
        if (team.players && Array.isArray(team.players)) {
          allPlayers.push(...team.players);
        }
      });

      setFilteredPlayers(allPlayers.sort((a, b) => a.game_name.localeCompare(b.game_name)));
    } else {
      setFilteredPlayers([]);
    }

    // Note: fetchMatchDataFixtures is handled by useEffect for match-data tab
  };

  const handleTeamChange = (e) => {
    setSelectedTeam(e.target.value);
  };

  const handleUpdateTeam = async () => {
    setUpdating(true);

    try {
      const teamData = {
        season_id: selectedSeason,
        player_id: selectedPlayer,
        team_id: selectedTeam,
      };

      await apiService.createSeasonTeam(teamData);

      // Refresh season teams data in store
      await useStore.getState().fetchSeasonTeams();

      showToast('Team updated successfully!', 'success');
      setSelectedPlayer('');
      setSelectedTeam('');
      setSelectedSeason('');
    } catch (error) {
      console.error('Error updating team:', error);
      const errorMessage = error.response?.data?.Message || error.message || 'Failed to update team';
      showToast(`Error: ${errorMessage}`, 'error');
    } finally {
      setUpdating(false);
    }
  };

  const handleCreateMatchData = async () => {
    setUpdating(true);

    try {
      // Validate required fields
      if (!selectedAvailableMatch) {
        showToast('Please select a match', 'error');
        setUpdating(false);
        return;
      }

      if (!selectedSeason) {
        showToast('Please select a season', 'error');
        setUpdating(false);
        return;
      }

      if (!selectedGroup) {
        showToast('Please select a group', 'error');
        setUpdating(false);
        return;
      }

      // Create match data object
      const matchData = {
        match_id: selectedAvailableMatch,
        season_id: selectedSeason,
        group_id: selectedGroup,
      };

      await apiService.createSeasonMatchData(matchData);

      showToast('Match data created successfully!', 'success');

      // Reset selections
      setSelectedPlayer('');
      setSelectedAvailableMatch('');
      setAvailableMatches([]);
      setSelectedGroup('');
    } catch (error) {
      console.error('Error creating match data:', error);
      const errorMessage = error.response?.data?.Message || error.message || 'Failed to create match data';
      showToast(`Error: ${errorMessage}`, 'error');
    } finally {
      setUpdating(false);
    }
  };

  const handleAssignTeamToGroup = async () => {
    setUpdating(true);

    try {
      const assignmentData = {
        season_id: selectedSeason,
        group_id: selectedGroup,
        team_id: selectedTeam,
      };

      await apiService.assignTeamToGroup(assignmentData);

      showToast('Team assigned to group successfully!', 'success');
      setSelectedTeam('');
      setSelectedGroup('');
    } catch (error) {
      console.error('Error assigning team to group:', error);
      const errorMessage = error.response?.data?.Message || error.message || 'Failed to assign team to group';
      showToast(`Error: ${errorMessage}`, 'error');
    } finally {
      setUpdating(false);
    }
  };

  const handleGroupChange = (e) => {
    setSelectedGroup(e.target.value);
  };

  const handleSponsorInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSponsorForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleCreateSponsor = async () => {
    if (!sponsorForm.name.trim()) {
      showToast('Sponsor name is required', 'error');
      return;
    }

    setIsSavingSponsor(true);
    try {
      if (isEditingSponsor && editingSponsorId) {
        const response = await apiService.updateSponsor(editingSponsorId, sponsorForm);
        const updatedSponsor = response.data;
        if (updatedSponsor) {
          setSponsors((prev) =>
            prev
              .map((sponsor) => (sponsor.id === editingSponsorId ? updatedSponsor : sponsor))
              .sort((a, b) => a.name.localeCompare(b.name))
          );
        }
        showToast('Sponsor updated successfully!', 'success');
      } else {
        const response = await apiService.createSponsor(sponsorForm);
        const createdSponsor = response.data;
        if (createdSponsor) {
          setSponsors((prev) => [...prev, createdSponsor].sort((a, b) => a.name.localeCompare(b.name)));
        }
        showToast('Sponsor created successfully!', 'success');
      }

      setSponsorForm({
        name: '',
        website: '',
        instagram: '',
        logo: '',
        is_active: true,
      });
      setIsEditingSponsor(false);
      setEditingSponsorId(null);
    } catch (error) {
      console.error('Error creating sponsor:', error);
      const errorMessage = error.response?.data?.Message || error.message || 'Failed to create sponsor';
      showToast(`Error: ${errorMessage}`, 'error');
    } finally {
      setIsSavingSponsor(false);
    }
  };

  const handleEditSponsor = (sponsor) => {
    setSponsorForm({
      name: sponsor.name || '',
      website: sponsor.website || '',
      instagram: sponsor.instagram || '',
      logo: sponsor.logo || '',
      is_active: sponsor.is_active !== false,
    });
    setIsEditingSponsor(true);
    setEditingSponsorId(sponsor.id);
  };

  const handleCancelSponsorEdit = () => {
    setSponsorForm({
      name: '',
      website: '',
      instagram: '',
      logo: '',
      is_active: true,
    });
    setIsEditingSponsor(false);
    setEditingSponsorId(null);
  };

  // Fixture Management Handlers
  const handleFixtureInputChange = (e) => {
    const { name, value } = e.target;
    setFixtureData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCreateFixture = async () => {
    setUpdating(true);

    try {
      // Validate required fields
      if (!fixtureData.season_id || !fixtureData.group_id) {
        showToast('Season and Group are required', 'error');
        setUpdating(false);
        return;
      }

      const matchData = {
        season_id: fixtureData.season_id,
        group_id: fixtureData.group_id,
        attacker_team_id: fixtureData.attacker_team_id || null,
        defender_team_id: fixtureData.defender_team_id || null,
        match_datetime: fixtureData.match_datetime ? `${fixtureData.match_datetime}:00` : null,
        venue: fixtureData.venue || null,
        venue_location: fixtureData.venue_location || null,
        youtube_location: fixtureData.youtube_location || null,
      };

      if (isEditMode && selectedFixture) {
        // Update existing fixture
        await apiService.updateMatch(selectedFixture.match_id, matchData);
        showToast('Fixture updated successfully!', 'success');

        // Refresh fixtures from store
        await fetchFixturesForSeason(fixtureData.season_id);
      } else {
        // Create new fixture
        await apiService.createMatch(matchData);
        showToast('Fixture created successfully!', 'success');

        // Refresh fixtures from store
        await fetchFixturesForSeason(fixtureData.season_id);
      }

      // Reset to create mode
      setIsEditMode(false);
      setSelectedFixture(null);
      setFixtureData({
        season_id: fixtureData.season_id, // Keep season selected
        group_id: fixtureData.group_id, // Keep group selected
        attacker_team_id: '',
        defender_team_id: '',
        match_datetime: '',
        venue: 'ONLINE',
        venue_location: '',
        youtube_location: '',
      });
    } catch (error) {
      console.error('Error creating/updating fixture:', error);
      const errorMessage = error.response?.data?.Message || error.message || 'Failed to save fixture';
      showToast(`Error: ${errorMessage}`, 'error');
    } finally {
      setUpdating(false);
    }
  };

  const handleFixtureSeasonChange = (e) => {
    const seasonId = e.target.value;
    setFixtureData((prev) => ({
      ...prev,
      season_id: seasonId,
      group_id: '',
      attacker_team_id: '',
      defender_team_id: '',
    }));

    // Fetch fixtures for this season
    if (seasonId) {
      fetchFixturesForSeason(seasonId);
    } else {
      setFixtures([]);
      setSelectedFixture(null);
    }
  };

  const fetchFixturesForSeason = async (seasonId) => {
    try {
      // Fetch from store
      await useStore.getState().fetchFixtures(seasonId);
      const fixturesData = useStore.getState().getFixtures(seasonId);

      console.log('Fixtures data from store:', fixturesData);

      if (!fixturesData) {
        setFixtures([]);
        return;
      }

      // Flatten all fixtures from grouped_fixtures and ungrouped_matches
      const allFixtures = [];

      fixturesData.grouped_fixtures.forEach((group) => {
        group.matches.forEach((match) => {
          allFixtures.push({
            ...match,
            group_name: group.group_name,
            group_id: group.group_id,
          });
        });
      });

      fixturesData.ungrouped_matches.forEach((match) => {
        allFixtures.push(match);
      });

      console.log('All fixtures flattened:', allFixtures);
      setFixtures(allFixtures);
    } catch (error) {
      console.error('Error fetching fixtures:', error);
      showToast('Failed to load fixtures', 'error');
    }
  };

  const handleFixtureSelect = (e) => {
    const matchId = e.target.value;

    if (!matchId) {
      // Clear selection - switch to create mode
      setSelectedFixture(null);
      setIsEditMode(false);
      setFixtureData({
        season_id: fixtureData.season_id,
        group_id: '',
        attacker_team_id: '',
        defender_team_id: '',
        match_datetime: '',
        venue: 'ONLINE',
        venue_location: '',
        youtube_location: '',
      });
      return;
    }

    const fixture = fixtures.find((f) => f.match_id === matchId);

    if (fixture) {
      setSelectedFixture(fixture);
      setIsEditMode(true);

      // Format datetime for input (if exists)
      let formattedDatetime = '';
      if (fixture.match_datetime) {
        // Extract just the datetime part without timezone conversion
        // API returns format like "2025-11-07T20:30:00"
        // datetime-local expects "YYYY-MM-DDTHH:mm"
        formattedDatetime = fixture.match_datetime.slice(0, 16);
      }

      setFixtureData({
        season_id: fixtureData.season_id,
        group_id: fixture.group_id || '',
        attacker_team_id: fixture.attacker_team?.id || '',
        defender_team_id: fixture.defender_team?.id || '',
        match_datetime: formattedDatetime,
        venue: fixture.venue || '',
        venue_location: fixture.venue_location || '',
        youtube_location: fixture.youtube_location || '',
      });
    }
  };

  const handleFixtureGroupChange = (e) => {
    const groupId = e.target.value;
    setFixtureData((prev) => ({
      ...prev,
      group_id: groupId,
    }));
  };

  // If logged in, show the admin dashboard
  if (isLoggedIn) {
    return (
      <div className="admin-container">
        <div className="admin-content">
          <div className="admin-dashboard">
            <div className="dashboard-section">
              <h2 className="section-title">Admin Panel</h2>

              {/* Management Tabs */}
              <div className="management-tabs">
                <div
                className={`management-tab ${activeTab === 'auction-setup' ? 'active' : ''}`}
                onClick={() => setActiveTab('auction-setup')}
>
                <span className="tab-name">⚡ Auction Setup</span>
                </div>
                <div
                  className={`management-tab ${activeTab === 'player-management' ? 'active' : ''}`}
                  onClick={() => setActiveTab('player-management')}
                >
                  <span className="tab-name">Player Management</span>
                </div>
                <div
                  className={`management-tab ${activeTab === 'team-management' ? 'active' : ''}`}
                  onClick={() => setActiveTab('team-management')}
                >
                  <span className="tab-name">Team Management</span>
                </div>
                <div
                  className={`management-tab ${activeTab === 'group-management' ? 'active' : ''}`}
                  onClick={() => setActiveTab('group-management')}
                >
                  <span className="tab-name">Group Management</span>
                </div>
                <div
                  className={`management-tab ${activeTab === 'fixture-management' ? 'active' : ''}`}
                  onClick={() => setActiveTab('fixture-management')}
                >
                  <span className="tab-name">Fixture Management</span>
                </div>
                <div
                  className={`management-tab ${activeTab === 'sponsor-management' ? 'active' : ''}`}
                  onClick={() => setActiveTab('sponsor-management')}
                >
                  <span className="tab-name">Sponsor Management</span>
                </div>
                <div
                  className={`management-tab ${activeTab === 'match-data' ? 'active' : ''}`}
                  onClick={() => {
                    setActiveTab('match-data');
                  }}
                >
                  <span className="tab-name">Match Data</span>
                </div>
              </div>

              {/* Player Verification Section */}
              {activeTab === 'player-management' && (
                <div className="player-verification-section">
                  <div className="pv-header">
                    <h3 className="pv-title">Player Verification</h3>
                    <p className="pv-subtitle">Verify players to add them to the auction pool. Only verified players appear in auction setup.</p>
                  </div>
                  <div className="pv-controls">
                    <select
                      className="dropdown-select"
                      value={verificationSeason}
                      onChange={(e) => {
                        setVerificationSeason(e.target.value);
                        loadVerificationPlayers(e.target.value);
                      }}
                    >
                      <option value="">Select season…</option>
                      {seasons.map(s => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))}
                    </select>
                    <input
                      className="pv-search"
                      placeholder="Search player…"
                      value={verificationSearch}
                      onChange={e => setVerificationSearch(e.target.value)}
                    />
                    <span className="pv-count">
                      {verificationPlayers.filter(p => p.verified).length}/{verificationPlayers.length} verified
                    </span>
                  </div>
                  {verificationLoading && <p className="pv-loading">Loading…</p>}
                  {!verificationLoading && verificationPlayers.length > 0 && (
                    <div className="pv-list">
                      {verificationPlayers
                        .filter(p => !verificationSearch || (p.game_name || '').toLowerCase().includes(verificationSearch.toLowerCase()))
                        .map(p => (
                          <div key={p.id} className={`pv-row ${p.verified ? 'pv-row-verified' : ''}`}>
                            <div className="pv-player-info">
                              <span className="pv-game-name">{p.game_name || p.valo_id}</span>
                              <span className="pv-meta">{p.prim_role} · {p.peak_rank}</span>
                            </div>
                            <span className={`pv-status ${p.verified ? 'pv-verified' : 'pv-unverified'}`}>
                              {p.verified ? '✓ Verified' : 'Unverified'}
                            </span>
                            <button
                              className={`pv-btn ${p.verified ? 'pv-btn-unverify' : 'pv-btn-verify'}`}
                              onClick={() => handleVerifyToggle(p.id, p.verified, p.game_name)}
                            >
                              {p.verified ? 'Unverify' : 'Verify'}
                            </button>
                          </div>
                        ))}
                    </div>
                  )}
                  {!verificationLoading && verificationSeason && verificationPlayers.length === 0 && (
                    <p className="pv-empty">No players registered for this season yet.</p>
                  )}
                </div>
              )}

              {/* Team Management Section */}
              {activeTab === 'team-management' && (
                <div className="team-management-section">
                  <div className="dropdown-container">
                    <div className="dropdown-group">
                      <label htmlFor="team-season-select" className="dropdown-label">
                        Select Season:
                      </label>
                      <select
                        id="team-season-select"
                        value={selectedSeason}
                        onChange={handleSeasonChange}
                        className="dropdown-select"
                      >
                        <option value="">Choose a season...</option>
                        {seasons.map((season) => (
                          <option key={season.id} value={season.id}>
                            {season.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="dropdown-group">
                      <label htmlFor="team-player-select" className="dropdown-label">
                        Select Player:
                      </label>
                      <select
                        id="team-player-select"
                        value={selectedPlayer}
                        onChange={handlePlayerChange}
                        className="dropdown-select"
                        disabled={!selectedSeason}
                      >
                        <option value="">{selectedSeason ? 'Choose a player...' : 'Select a season first'}</option>
                        {filteredPlayers.map((player) => (
                          <option key={player.id} value={player.id}>
                            {player.game_name || player.valo_id}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="dropdown-group">
                      <label htmlFor="team-select" className="dropdown-label">
                        Select Team:
                      </label>
                      <select
                        id="team-select"
                        value={selectedTeam}
                        onChange={handleTeamChange}
                        className="dropdown-select"
                        disabled={!selectedSeason}
                      >
                        <option value="">{selectedSeason ? 'Choose a team...' : 'Select a season first'}</option>
                        {teams
                          .sort((a, b) => a.name.localeCompare(b.name))
                          .map((team) => (
                            <option key={team.id} value={team.id}>
                              {team.name}
                            </option>
                          ))}
                      </select>
                    </div>

                    {selectedPlayer && selectedTeam && selectedSeason && (
                      <div className="view-data-section">
                        <button
                          className={`view-data-button ${isUpdating ? 'loading' : ''}`}
                          onClick={handleUpdateTeam}
                          disabled={isUpdating}
                        >
                          {isUpdating ? (
                            <>
                              <span className="spinner"></span>
                              Updating...
                            </>
                          ) : (
                            'Update Team'
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Group Management Section */}
              {activeTab === 'group-management' && (
                <div className="group-management-section">
                  <h3 className="subsection-title">Assign Teams to Groups</h3>
                  <div className="dropdown-container">
                    <div className="dropdown-group">
                      <label htmlFor="group-season-select" className="dropdown-label">
                        Select Season:
                      </label>
                      <select
                        id="group-season-select"
                        value={selectedSeason}
                        onChange={handleSeasonChange}
                        className="dropdown-select"
                      >
                        <option value="">Choose a season...</option>
                        {seasons.map((season) => (
                          <option key={season.id} value={season.id}>
                            {season.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="dropdown-group">
                      <label htmlFor="group-select" className="dropdown-label">
                        Select Group/Stage:
                      </label>
                      <select
                        id="group-select"
                        value={selectedGroup}
                        onChange={handleGroupChange}
                        className="dropdown-select"
                        disabled={!selectedSeason}
                      >
                        <option value="">{selectedSeason ? 'Choose a group...' : 'Select a season first'}</option>
                        {groups
                          .filter((group) => group.stages === true)
                          .map((group) => (
                            <option key={group.id} value={group.id}>
                              {group.name} {group.match_format && `(${group.match_format})`}
                            </option>
                          ))}
                      </select>
                    </div>

                    <div className="dropdown-group">
                      <label htmlFor="group-team-select" className="dropdown-label">
                        Select Team:
                      </label>
                      <select
                        id="group-team-select"
                        value={selectedTeam}
                        onChange={handleTeamChange}
                        className="dropdown-select"
                        disabled={!selectedSeason}
                      >
                        <option value="">{selectedSeason ? 'Choose a team...' : 'Select a season first'}</option>
                        {teams
                          .sort((a, b) => a.name.localeCompare(b.name))
                          .map((team) => (
                            <option key={team.id} value={team.id}>
                              {team.name}
                            </option>
                          ))}
                      </select>
                    </div>

                    {selectedGroup && selectedTeam && selectedSeason && (
                      <div className="view-data-section">
                        <button
                          className={`view-data-button ${isUpdating ? 'loading' : ''}`}
                          onClick={handleAssignTeamToGroup}
                          disabled={isUpdating}
                        >
                          {isUpdating ? (
                            <>
                              <span className="spinner"></span>
                              Assigning...
                            </>
                          ) : (
                            'Assign Team to Group'
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Fixture Management Section */}
              {activeTab === 'fixture-management' && (
                <div className="fixture-management-section">
                  <h3 className="subsection-title">{isEditMode ? 'Update Match Fixture' : 'Create Match Fixture'}</h3>
                  <div className="dropdown-container">
                    <div className="dropdown-group">
                      <label htmlFor="fixture-season-select" className="dropdown-label">
                        Select Season: <span className="required">*</span>
                      </label>
                      <select
                        id="fixture-season-select"
                        name="season_id"
                        value={fixtureData.season_id}
                        onChange={handleFixtureSeasonChange}
                        className="dropdown-select"
                      >
                        <option value="">Choose a season...</option>
                        {seasons.map((season) => (
                          <option key={season.id} value={season.id}>
                            {season.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {fixtureData.season_id && (
                      <div className="dropdown-group">
                        <label htmlFor="fixture-select" className="dropdown-label">
                          Select Existing Fixture to Edit:
                        </label>
                        <select
                          id="fixture-select"
                          value={selectedFixture?.match_id || ''}
                          onChange={handleFixtureSelect}
                          className="dropdown-select"
                        >
                          <option value="">Create New Fixture</option>
                          {fixtures.length > 0 ? (
                            fixtures.map((fixture) => {
                              const attackerName = fixture.attacker_team?.name || 'TBD';
                              const defenderName = fixture.defender_team?.name || 'TBD';
                              const groupName = fixture.group_name || 'No Group';
                              const dateStr = fixture.match_datetime
                                ? new Date(fixture.match_datetime).toLocaleString()
                                : 'No Date Set';
                              const status = fixture.status || 'upcoming';

                              return (
                                <option key={fixture.match_id} value={fixture.match_id}>
                                  [{status.toUpperCase()}] {groupName}: {attackerName} vs {defenderName} - {dateStr}
                                </option>
                              );
                            })
                          ) : (
                            <option disabled>No fixtures found for this season</option>
                          )}
                        </select>
                      </div>
                    )}

                    <div className="dropdown-group">
                      <label htmlFor="fixture-group-select" className="dropdown-label">
                        Select Group/Stage: <span className="required">*</span>
                      </label>
                      <select
                        id="fixture-group-select"
                        name="group_id"
                        value={fixtureData.group_id}
                        onChange={handleFixtureGroupChange}
                        className="dropdown-select"
                        disabled={!fixtureData.season_id}
                      >
                        <option value="">
                          {fixtureData.season_id ? 'Choose a group...' : 'Select a season first'}
                        </option>
                        {groups.map((group) => (
                          <option key={group.id} value={group.id}>
                            {group.name} {group.match_format && `(${group.match_format})`}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="dropdown-group">
                      <label htmlFor="fixture-datetime" className="dropdown-label">
                        Match Date & Time: <span className="optional-label">(Optional)</span>
                      </label>
                      <input
                        type="datetime-local"
                        id="fixture-datetime"
                        name="match_datetime"
                        value={fixtureData.match_datetime}
                        onChange={handleFixtureInputChange}
                        className="form-input"
                      />
                    </div>

                    <div className="dropdown-group">
                      <label htmlFor="fixture-attacker-team" className="dropdown-label">
                        Attacker Team: <span className="optional-label">(Optional - leave empty for TBD)</span>
                      </label>
                      <select
                        id="fixture-attacker-team"
                        name="attacker_team_id"
                        value={fixtureData.attacker_team_id}
                        onChange={handleFixtureInputChange}
                        className="dropdown-select"
                        disabled={!fixtureData.season_id}
                      >
                        <option value="">TBD (To Be Determined)</option>
                        {teams
                          .sort((a, b) => a.name.localeCompare(b.name))
                          .map((team) => (
                            <option key={team.id} value={team.id}>
                              {team.name}
                            </option>
                          ))}
                      </select>
                    </div>

                    <div className="dropdown-group">
                      <label htmlFor="fixture-defender-team" className="dropdown-label">
                        Defender Team: <span className="optional-label">(Optional - leave empty for TBD)</span>
                      </label>
                      <select
                        id="fixture-defender-team"
                        name="defender_team_id"
                        value={fixtureData.defender_team_id}
                        onChange={handleFixtureInputChange}
                        className="dropdown-select"
                        disabled={!fixtureData.season_id}
                      >
                        <option value="">TBD (To Be Determined)</option>
                        {teams
                          .sort((a, b) => a.name.localeCompare(b.name))
                          .map((team) => (
                            <option key={team.id} value={team.id}>
                              {team.name}
                            </option>
                          ))}
                      </select>
                    </div>

                    <div className="dropdown-group">
                      <label htmlFor="fixture-venue" className="dropdown-label">
                        Venue:
                      </label>
                      <select
                        id="fixture-venue"
                        name="venue"
                        value={fixtureData.venue || 'ONLINE'}
                        onChange={handleFixtureInputChange}
                        className="dropdown-select"
                      >
                        <option value="ONLINE">ONLINE</option>
                        <option value="OFFLINE">OFFLINE</option>
                      </select>
                    </div>

                    <div className="dropdown-group">
                      <label htmlFor="fixture-venue-location" className="dropdown-label">
                        Venue Location: <span className="optional-label">(Optional)</span>
                      </label>
                      <input
                        type="text"
                        id="fixture-venue-location"
                        name="venue_location"
                        value={fixtureData.venue_location}
                        onChange={handleFixtureInputChange}
                        placeholder="e.g., Mumbai, India"
                        className="form-input"
                      />
                    </div>

                    <div className="dropdown-group">
                      <label htmlFor="fixture-youtube" className="dropdown-label">
                        YouTube Location: <span className="optional-label">(Optional)</span>
                      </label>
                      <input
                        type="text"
                        id="fixture-youtube"
                        name="youtube_location"
                        value={fixtureData.youtube_location}
                        onChange={handleFixtureInputChange}
                        placeholder="YouTube video ID or URL"
                        className="form-input"
                      />
                    </div>

                    {fixtureData.season_id && fixtureData.group_id && (
                      <div className="view-data-section">
                        <button
                          className={`view-data-button ${isUpdating ? 'loading' : ''}`}
                          onClick={handleCreateFixture}
                          disabled={isUpdating}
                        >
                          {isUpdating ? (
                            <>
                              <span className="spinner"></span>
                              {isEditMode ? 'Updating Fixture...' : 'Creating Fixture...'}
                            </>
                          ) : isEditMode ? (
                            'Update Fixture'
                          ) : (
                            'Create Fixture'
                          )}
                        </button>
                        {isEditMode && (
                          <button
                            className="view-data-button cancel-button"
                            onClick={() => {
                              setIsEditMode(false);
                              setSelectedFixture(null);
                              setFixtureData({
                                season_id: fixtureData.season_id,
                                group_id: '',
                                attacker_team_id: '',
                                defender_team_id: '',
                                match_datetime: '',
                                venue: 'ONLINE',
                                venue_location: '',
                                youtube_location: '',
                              });
                            }}
                            disabled={isUpdating}
                          >
                            Cancel Edit
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'sponsor-management' && (
                <div className="sponsor-management-section">
                  <h3 className="subsection-title">{isEditingSponsor ? 'Edit Sponsor' : 'Add Sponsor'}</h3>
                  <div className="form-section">
                    <div className="form-group">
                      <label htmlFor="sponsor-name" className="form-label">
                        Sponsor Name <span className="required">*</span>
                      </label>
                      <input
                        type="text"
                        id="sponsor-name"
                        name="name"
                        value={sponsorForm.name}
                        onChange={handleSponsorInputChange}
                        placeholder="e.g., PGEsports"
                        className="form-input"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="sponsor-website" className="form-label">
                        Website
                      </label>
                      <input
                        type="text"
                        id="sponsor-website"
                        name="website"
                        value={sponsorForm.website}
                        onChange={handleSponsorInputChange}
                        placeholder="https://example.com"
                        className="form-input"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="sponsor-instagram" className="form-label">
                        Instagram
                      </label>
                      <input
                        type="text"
                        id="sponsor-instagram"
                        name="instagram"
                        value={sponsorForm.instagram}
                        onChange={handleSponsorInputChange}
                        placeholder="@handle or https://instagram.com/handle"
                        className="form-input"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="sponsor-logo" className="form-label">
                        Logo URL
                      </label>
                      <input
                        type="text"
                        id="sponsor-logo"
                        name="logo"
                        value={sponsorForm.logo}
                        onChange={handleSponsorInputChange}
                        placeholder="https://.../logo.png"
                        className="form-input"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="sponsor-active" className="form-label checkbox-label">
                        <input
                          type="checkbox"
                          id="sponsor-active"
                          name="is_active"
                          checked={sponsorForm.is_active}
                          onChange={handleSponsorInputChange}
                        />
                        Active Sponsor
                      </label>
                    </div>

                    <div className="view-data-section">
                      <button
                        className={`view-data-button ${isSavingSponsor ? 'loading' : ''}`}
                        onClick={handleCreateSponsor}
                        disabled={isSavingSponsor}
                      >
                        {isSavingSponsor ? (
                          <>
                            <span className="spinner"></span>
                            Saving...
                          </>
                        ) : isEditingSponsor ? (
                          'Update Sponsor'
                        ) : (
                          'Create Sponsor'
                        )}
                      </button>
                      {isEditingSponsor && (
                        <button className="view-data-button cancel-button" onClick={handleCancelSponsorEdit}>
                          Cancel
                        </button>
                      )}
                    </div>
                  </div>

                  <h3 className="subsection-title">Active Sponsors</h3>
                  <div className="sponsor-list">
                    {sponsors.length === 0 && <div className="empty-state">No sponsors yet.</div>}
                    {sponsors.map((sponsor) => (
                      <div key={sponsor.id} className="sponsor-row">
                        <div className="sponsor-info">
                          <span className="sponsor-name">{sponsor.name}</span>
                          <span className="sponsor-meta">
                            {sponsor.instagram || sponsor.website || 'No link provided'}
                          </span>
                        </div>
                        <div className="sponsor-actions">
                          <span className={`status-pill ${sponsor.is_active ? 'active' : 'inactive'}`}>
                            {sponsor.is_active ? 'Active' : 'Inactive'}
                          </span>
                          <button className="sponsor-edit" onClick={() => handleEditSponsor(sponsor)}>
                            Edit
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

            

               {activeTab === 'auction-setup' && (
  <div className="match-data-section">
    <div className="dropdown-container">

      <div className="dropdown-group">
        <label className="dropdown-label">Season <span className="required">*</span></label>
        <select
          className="dropdown-select"
          value={auctionSetup.season_id}
          onChange={(e) => setAuctionSetup((p) => ({ ...p, season_id: e.target.value }))}
        >
          <option value="">Choose season…</option>
          {seasons.map((s) => (
            <option key={s.id} value={s.id}>{s.name}</option>
          ))}
        </select>
      </div>

      <div className="dropdown-group">
        <label className="dropdown-label">Number of Sets <span className="optional-label">(default 5)</span></label>
        <input
          type="number"
          min={1}
          max={20}
          className="form-input"
          value={auctionSetup.num_sets}
          onChange={(e) => setAuctionSetup((p) => ({ ...p, num_sets: parseInt(e.target.value) || 5 }))}
        />
      </div>

      <div className="dropdown-group">
        <label className="dropdown-label">Budget per Team (pts) <span className="optional-label">(default 150)</span></label>
        <input
          type="number"
          min={1}
          className="form-input"
          value={auctionSetup.budget_per_team}
          onChange={(e) => setAuctionSetup((p) => ({ ...p, budget_per_team: parseInt(e.target.value) || 150 }))}
        />
      </div>

      <div className="dropdown-group">
        <label className="dropdown-label">Auction Admin Password <span className="required">*</span></label>
        <input
          type="password"
          className="form-input"
          placeholder="Enter auction admin password"
          value={auctionSetup.admin_password}
          onChange={(e) => setAuctionSetup((p) => ({ ...p, admin_password: e.target.value }))}
          autoComplete="off"
        />
      </div>

      <div className="view-data-section">
        <button
          className={`view-data-button ${isRunningSetup ? 'loading' : ''}`}
          disabled={isRunningSetup || !auctionSetup.season_id || !auctionSetup.admin_password}
          onClick={async () => {
            setIsRunningSetup(true);
            setAuctionSetupResult(null);
            try {
              const res = await apiService.auctionSetup(auctionSetup);
              setAuctionSetupResult({ ok: true, data: res.data });
              showToast(`Setup complete! ${res.data.total_players} players across ${res.data.num_sets} sets.`, 'success');
            } catch (err) {
              const msg = err?.response?.data?.Message || err?.response?.data?.message || err.message || 'Setup failed';
              setAuctionSetupResult({ ok: false, msg });
              showToast(`Error: ${msg}`, 'error');
            } finally {
              setIsRunningSetup(false);
            }
          }}
        >
          {isRunningSetup ? <><span className="spinner"></span> Running…</> : '⚡ Run Auction Setup'}
        </button>
      </div>
    </div>

    {auctionSetupResult?.ok && (
      <div style={{ marginTop: '24px', padding: '20px', background: '#0d1f17', border: '1px solid #1e3a2e', borderRadius: '12px' }}>
        <p style={{ color: '#3ce2c2', fontWeight: 700, marginBottom: '12px' }}>✓ Setup Successful</p>
        <p style={{ color: '#64748b', fontSize: '13px' }}>Session ID: <code style={{ background: '#1e293b', padding: '2px 6px', borderRadius: '4px', color: '#94a3b8' }}>{auctionSetupResult.data.session_id}</code></p>
        <p style={{ color: '#94a3b8', fontSize: '13px', marginTop: '8px' }}>
          {auctionSetupResult.data.total_players} players · {auctionSetupResult.data.num_sets} sets · {auctionSetupResult.data.teams_initialized} teams initialized
        </p>
        <p style={{ color: '#475569', fontSize: '12px', marginTop: '12px' }}>
          ✅ Verify in Supabase: auction_sessions, auction_sets, auction_players, auction_team_budgets all have rows.
        </p>
      </div>
    )}

    {auctionSetupResult && !auctionSetupResult.ok && (
      <div style={{ marginTop: '20px', padding: '14px', background: '#1a0e0e', border: '1px solid #ef4444', borderRadius: '8px', color: '#ef4444', fontSize: '13px' }}>
        ❌ {auctionSetupResult.msg}
      </div>
    )}
  </div>
)}

              {/* Match Data Dropdown */}
              {activeTab === 'match-data' && (
                <div className="match-data-section">
                  <div className="dropdown-container">
                    <div className="dropdown-group">
                      <label htmlFor="season-select" className="dropdown-label">
                        Select Season:
                      </label>
                      <select
                        id="season-select"
                        value={selectedSeason}
                        onChange={handleSeasonChange}
                        className="dropdown-select"
                      >
                        <option value="">Choose a season...</option>
                        {seasons.map((season) => (
                          <option key={season.id} value={season.id}>
                            {season.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="dropdown-group">
                      <label htmlFor="group-select-match" className="dropdown-label">
                        Select Group/Stage:
                      </label>
                      <select
                        id="group-select-match"
                        value={selectedGroup}
                        onChange={handleGroupChange}
                        className="dropdown-select"
                        disabled={!selectedSeason}
                      >
                        <option value="">{selectedSeason ? 'Choose a group...' : 'Select a season first'}</option>
                        {groups.map((group) => (
                          <option key={group.id} value={group.id}>
                            {group.name} {group.match_format && `(${group.match_format})`}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="dropdown-group">
                      <label htmlFor="player-select" className="dropdown-label">
                        Select Player:
                      </label>
                      <select
                        id="player-select"
                        value={selectedPlayer}
                        onChange={handlePlayerChange}
                        className="dropdown-select"
                        disabled={!selectedSeason}
                      >
                        <option value="">{selectedSeason ? 'Choose a player...' : 'Select a season first'}</option>
                        {filteredPlayers.map((player) => (
                          <option key={player.id} value={player.id}>
                            {player.game_name || player.valo_id}
                          </option>
                        ))}
                      </select>
                    </div>

                    {selectedPlayer && selectedSeason && selectedGroup && availableMatches.length === 0 && (
                      <div className="view-data-section">
                        <button
                          className="view-data-button"
                          onClick={handleFetchPlayerMatches}
                          disabled={isLoadingMatches}
                        >
                          {!isLoadingMatches ? 'Fetch Player Matches' : 'Loading matches...'}
                        </button>
                      </div>
                    )}
                  </div>

                  {availableMatches.length > 0 && (
                    <div className="matches-list-section">
                      <h3 className="subsection-title">Available Matches - Click to Select:</h3>
                      <div className="matches-list">
                        {availableMatches.map((match) => (
                          <div
                            key={match.match_id}
                            className={`match-item ${selectedAvailableMatch === match.match_id ? 'selected' : ''}`}
                            onClick={() => setSelectedAvailableMatch(match.match_id)}
                          >
                            <div className="match-info-admin">
                              <span className="match-id">{match.match_id}</span>
                              <span className="match-separator">-</span>
                              <span className="match-map">{match.map}</span>
                              <span className="match-separator">-</span>
                              <span className="match-score">
                                {match.team_score}:{match.enemy_score}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {selectedPlayer && selectedSeason && selectedGroup && selectedAvailableMatch && (
                    <div className="view-data-section">
                      <button
                        className={`view-data-button ${isUpdating ? 'loading' : ''}`}
                        onClick={handleCreateMatchData}
                        disabled={isUpdating}
                      >
                        {isUpdating ? (
                          <>
                            <span className="spinner"></span>
                            Creating...
                          </>
                        ) : (
                          'Create Match Data'
                        )}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Toast Notification */}
        {toast && <Toast message={toast.message} type={toast.type} onClose={hideToast} />}
      </div>
    );
  }

  // Login form
  return (
    <div className="admin-container login-view">
      <div className="admin-content">
        <div className="admin-header">
          <h1 className="admin-title">Admin Login</h1>
          <p className="admin-subtitle">Access the VPL Admin Dashboard</p>
        </div>

        <form onSubmit={handleSubmit} className="admin-form">
          <div className="form-section">
            <h2 className="section-title">Administrator Access</h2>

            <div className="form-group">
              <label htmlFor="userid" className="form-label">
                User ID <span className="required">*</span>
              </label>
              <input
                type="text"
                id="userid"
                name="userid"
                value={formData.userid}
                onChange={handleInputChange}
                placeholder="Enter your user ID"
                className={`form-input ${errors.userid ? 'error' : ''}`}
              />
              {errors.userid && <span className="error-message">{errors.userid}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="password" className="form-label">
                Password <span className="required">*</span>
              </label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                placeholder="Enter your password"
                className={`form-input ${errors.password ? 'error' : ''}`}
              />
              {errors.password && <span className="error-message">{errors.password}</span>}
            </div>
          </div>

          {/* Submit Button */}
          <div className="form-submit">
            <button
              type="submit"
              disabled={isSubmitting}
              className={`submit-button ${isSubmitting ? 'submitting' : ''}`}
            >
              {isSubmitting ? (
                <>
                  <span className="spinner"></span>
                  Logging in...
                </>
              ) : (
                'Login to Admin Panel'
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Toast Notification */}
      {toast && <Toast message={toast.message} type={toast.type} onClose={hideToast} />}
    </div>
  );
}

export default Admin;